// ChannelNotificationBell.jsx

import React, { useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Services
import {
  CHANNEL_NOTIFICATION_MODE_OPTIONS,
  CHANNEL_NOTIFICATION_MODES,
  normalizeChannelNotificationMode,
} from 'channels/services/ChannelNotificationPrefService';

// CSS
import './ChannelNotificationBell.css';

// Components
import Button from 'components/button/Button';
import Popup from 'components/popup/Popup';
import PopupOption from 'components/popup/option/PopupOption';

/**
 * ChannelNotificationBell
 *
 * Slack-style bell control for per-channel notification presets.
 *
 * @param {string} mode - Current mode (`all` | `mentions` | `mute`).
 * @param {boolean} [busy=false] - Disables the trigger while saving.
 * @param {function} onModeChange - Called with the next mode id.
 *
 * @returns {ReactNode}
 */
const ChannelNotificationBell = React.memo(({
  mode = CHANNEL_NOTIFICATION_MODES.MENTIONS,
  busy = false,
  onModeChange,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- REFS ----- //

  const anchorRef = useRef(null);

  // ----- STATE ----- //

  const [popupOpen, setPopupOpen] = useState(false);

  // ----- MEMOS ----- //

  const currentMode = useMemo(
    () => normalizeChannelNotificationMode(mode),
    [mode]
  );

  const currentOption = useMemo(
    () => CHANNEL_NOTIFICATION_MODE_OPTIONS.find((row) => row.mode === currentMode)
      || CHANNEL_NOTIFICATION_MODE_OPTIONS[1],
    [currentMode]
  );

  const triggerTitle = `Notifications: ${currentOption.label}`;

  // ----- HANDLERS ----- //

  /**
   * Opens or closes the preset menu.
   */
  const handleTogglePopup = () => {
    if (busy) return;
    setPopupOpen((prev) => !prev);
  };

  /**
   * Applies a preset and closes the menu.
   *
   * @param {string} nextMode
   */
  const handleSelectMode = (nextMode) => {
    const cleanMode = normalizeChannelNotificationMode(nextMode);
    setPopupOpen(false);
    if (cleanMode === currentMode) return;
    onModeChange?.(cleanMode);
  };

  // ----- RENDER ----- //

  return (
    <div className="channel-notification-bell-container">
      <div
        className="channel-notification-bell-anchor"
        ref={anchorRef}
      >
        <Button
          iconKey="notifications"
          iconColor={currentMode === CHANNEL_NOTIFICATION_MODES.MUTE
            ? theme.outline
            : theme.foregroundMuted}
          iconSize={20}
          height="36px"
          width="36px"
          padding="0"
          backgroundColor="transparent"
          hoverBackgroundColor={theme.buttonHoverBackgroundColor}
          hoverForegroundColor={theme.foreground}
          borderRadius="8px"
          title={popupOpen ? undefined : triggerTitle}
          enabled={!busy}
          onClick={handleTogglePopup}
        />
      </div>

      <Popup
        anchorRef={anchorRef}
        isOpen={popupOpen}
        setOpen={setPopupOpen}
        align="right"
        placement="bottom"
      >
        <div className="channel-notification-bell-menu">
          {CHANNEL_NOTIFICATION_MODE_OPTIONS.map((option) => (
            <PopupOption
              key={option.mode}
              label={option.label}
              note={option.note}
              selected={option.mode === currentMode}
              onClick={() => handleSelectMode(option.mode)}
            />
          ))}
        </div>
      </Popup>
    </div>
  );
});

ChannelNotificationBell.displayName = 'ChannelNotificationBell';

export default ChannelNotificationBell;
